import { Payer } from "@redotech/cockroachdb-codegen/congodb/payer";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { parseGid } from "@redotech/redo-shopify-common/gid-utils";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import { Card } from "@redotech/redo-web/card";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { nullishEqual, objectEqual, stringEqual } from "@redotech/util/equal";
import classNames from "classnames";
import { memo, useContext } from "react";
import { RedoAdminClientContext } from "../../../client/context";
import { getCollections } from "../../../client/team";
import { TeamContext } from "../../team";

export const nonShopifyClaimsRecyclingForm = groupInput({
  enableRecycling: input<boolean>(),
  nonShopifyClaimsCollection: input<{ id: string; name: string } | undefined>({
    equal: nullishEqual(objectEqual({ id: stringEqual, name: stringEqual })),
  }),
  nonShopifyClaimsDisableEditQuantity: input<boolean>(),
  recyclingProductId: input<string>(),
  labelPayer: input<Payer>(),
  chargeMerchantPerRecycleRequest: input<boolean>(),
  perRecycleRequestFee: input<string>(),
  minRecyclingProductQuantity: input<string>(),
});

export type NonShopifyClaimsRecyclingForm = InputSchema.Input<
  typeof nonShopifyClaimsRecyclingForm
>;

export type NonShopifyClaimsRecyclingValue = InputSchema.Value<
  typeof nonShopifyClaimsRecyclingForm
>;

export const nonShopifyClaimsRecyclingDefault: NonShopifyClaimsRecyclingValue =
  {
    enableRecycling: false,
    nonShopifyClaimsCollection: undefined,
    nonShopifyClaimsDisableEditQuantity: false,
    recyclingProductId: "",
    labelPayer: Payer.Merchant,
    chargeMerchantPerRecycleRequest: false,
    perRecycleRequestFee: "3",
    minRecyclingProductQuantity: "5",
  };

export const NonShopifyClaimsRecycling = memo(
  function NonShopifyClaimsRecycling({
    input,
  }: {
    input: NonShopifyClaimsRecyclingForm;
  }) {
    const {
      enableRecycling,
      nonShopifyClaimsCollection,
      nonShopifyClaimsDisableEditQuantity,
      recyclingProductId,
      labelPayer,
      chargeMerchantPerRecycleRequest,
      perRecycleRequestFee,
      minRecyclingProductQuantity,
    } = input.inputs;

    const team = useContext(TeamContext);
    const client = useRequiredContext(RedoAdminClientContext);

    const collectionsLoad = useLoad(async () => {
      if (!team) {
        return { collections: [] };
      }
      const collections = await getCollections(client, { teamId: team._id });

      return {
        collections: collections.map((collection) => ({
          title: collection.title,
          id: String(parseGid(collection.id)),
        })),
      };
    }, [team, client]);

    const collectionsLoading =
      collectionsLoad.pending && !collectionsLoad.value;

    const handleSetValue = useHandler((collectionId: string | null) => {
      console.log({ collectionId, setValue: true });
      if (collectionId) {
        const collection = collectionsLoad.value?.collections.find(
          (c) => c.id === collectionId,
        );
        if (collection) {
          nonShopifyClaimsCollection.setValue({
            id: collection.id,
            name: collection.title,
          });
        }
      }
    });

    return (
      <Card title="Recycling">
        <section className={gridCss.grid}>
          <div className={classNames(gridCss.span12, gridCss.span6M)}>
            <RedoToggle
              description="Allow customers to recycle products through the Return Portal."
              label="Enable recycling"
              setValue={enableRecycling.setValue}
              size="sm"
              value={enableRecycling.value}
            />
          </div>
          <div className={classNames(gridCss.span12, gridCss.span6M)}>
            <RedoToggle
              description="Prevent customers from editing the quantity of item they're recycling."
              label="Hide quantity input for Recycling"
              setValue={nonShopifyClaimsDisableEditQuantity.setValue}
              size="sm"
              value={nonShopifyClaimsDisableEditQuantity.value}
            />
          </div>

          <div className={gridCss.span12}>
            <LabeledInput label="Collection for recyclable products">
              <Autocomplete
                getLabel={(collection) =>
                  collectionsLoad.value?.collections.find(
                    (c) => c.id === collection,
                  )?.title ?? ""
                }
                keyFn={(id) => id}
                noOptionsText={collectionsLoading ? "Loading..." : "No options"}
                onInputChange={() => {}}
                options={
                  collectionsLoad.value?.collections.map((c) => c.id) ?? []
                }
                value={nonShopifyClaimsCollection.value?.id}
                valueChange={handleSetValue}
              >
                {(id) => {
                  const displayString =
                    collectionsLoad.value?.collections.find((c) => c.id === id)
                      ?.title ?? undefined;
                  console.log({ displayString });
                  return displayString;
                }}
              </Autocomplete>
            </LabeledInput>
          </div>
          <div className={gridCss.span12}>
            <FormTextInput
              description="Enter the Shopify product ID for the recycling product."
              input={recyclingProductId}
              label="Recycling product ID"
              placeholder="gid://shopify/Product/..."
            />
          </div>
          <div className={classNames(gridCss.span12, gridCss.span6M)}>
            <FormSelectDropdown
              input={labelPayer}
              label="Recycling return label payer"
              options={Object.values(Payer)}
            >
              {(payer) => {
                switch (payer) {
                  case Payer.Customer:
                    return "Customer";
                  case Payer.Merchant:
                    return "Merchant";
                  case Payer.Redo:
                    return "Redo";
                }
              }}
            </FormSelectDropdown>
          </div>
          <div className={classNames(gridCss.span12, gridCss.span6M)}>
            <RedoToggle
              label="Bill merchant for each recycle request"
              setValue={chargeMerchantPerRecycleRequest.setValue}
              size="sm"
              value={chargeMerchantPerRecycleRequest.value}
            />
          </div>
          {chargeMerchantPerRecycleRequest.value && (
            <div className={classNames(gridCss.span12, gridCss.span6M)}>
              <FormTextInput
                input={perRecycleRequestFee}
                label="Recycle request fee"
                placeholder="0.00"
                type="number"
              />
            </div>
          )}
          <div className={classNames(gridCss.span12, gridCss.span6M)}>
            <FormTextInput
              input={minRecyclingProductQuantity}
              label="Minimum products required for a recycling request"
              placeholder=""
              type="number"
            />
          </div>
        </section>
      </Card>
    );
  },
);
